// Unified employee service (for backward compatibility)
import { employeeList } from './services/employeeList.service';
import { employeeGet } from './services/employeeGet.service';
import { employeeUpdate } from './services/employeeUpdate.service';
import { employeeCreate } from './services/employeeCreate.service';
import { employeeLookupService } from './services/employeeLookup.service';
import { apiClient } from '../../core/apiClient';
import { API_CONFIG } from '../../core/apiConfig';
import type { EmployeeExportFormat } from '@/types/fileExport.types';
import { getAPIFormat } from '@/types/fileExport.types';
import type { Region, Commune } from '../geography';
import type { MaritalStatus } from '../catalog';
import type { ApiResponse } from '../../shared';
import { mapEmployeeExportFilters, type EmployeeExportFilters } from './mappers/employeeExport.mapper';
import type {
  EmployeeListParams,
  EmployeesPage,
  EnableDisableResult,
  LC_EnableDisableEmployeeIn,
  EmployeeProfile,
  LC_UpdateEmployeeOut,
  LC_CreateEmployeeIn,
  LC_GetEmployeeLookupIn,
  LC_GetEmployeeLookupOut,
} from './types';

/**
 * Unified EmployeeService that delegates to modular services
 * Maintains backward compatibility with existing code
 */
export class EmployeeService {
  // List operations
  async list(
    params: EmployeeListParams,
    signal?: AbortSignal,
  ): Promise<ApiResponse<EmployeesPage>> {
    return employeeList.list(params, signal);
  }

  async enableDisable(
    input: LC_EnableDisableEmployeeIn,
  ): Promise<ApiResponse<EnableDisableResult>> {
    return employeeList.enableDisable(input);
  }

  // Get operations
  async getProfile(employeeId: number): Promise<ApiResponse<EmployeeProfile>> {
    return employeeGet.getProfile(employeeId);
  }

  async getRegions(country: string): Promise<ApiResponse<Region[]>> {
    return employeeGet.getRegions(country);
  }

  async getCommunes(country: string, regionId: number): Promise<ApiResponse<Commune[]>> {
    return employeeGet.getCommunes(country, regionId);
  }

  async getMaritalStatuses(countryId: number): Promise<ApiResponse<MaritalStatus[]>> {
    return employeeGet.getMaritalStatuses(countryId);
  }

  // Update operations
  async update(profile: EmployeeProfile): Promise<ApiResponse<LC_UpdateEmployeeOut>> {
    return employeeUpdate.update(profile);
  }

  // Create operations
  async create(input: LC_CreateEmployeeIn): Promise<ApiResponse<void>> {
    return employeeCreate.create(input);
  }

  // Lookup operations
  async lookup(params: LC_GetEmployeeLookupIn): Promise<ApiResponse<LC_GetEmployeeLookupOut>> {
    return employeeLookupService.lookup(params);
  }

  // Export operations
  async exportEmployees(
    format: EmployeeExportFormat,
    filters: EmployeeExportFilters,
  ): Promise<ApiResponse<{ blob: Blob; filename: string }>> {
    const mappedFilters = mapEmployeeExportFilters(filters);
    const apiFormat = getAPIFormat(format);
    
    const queryParams = new URLSearchParams();
    queryParams.append('companyId', String(mappedFilters.companyId));
    queryParams.append('format', apiFormat);
    
    if (mappedFilters.searchText) {
      queryParams.append('searchText', mappedFilters.searchText);
    }
    
    if (mappedFilters.employeeStatus) {
      queryParams.append('employeeStatus', mappedFilters.employeeStatus);
    }

    if (mappedFilters.contractStatusFilter) {
      queryParams.append('contractStatusFilter', mappedFilters.contractStatusFilter);
    }

    const endpoint = `${API_CONFIG.ENDPOINTS.EMPLOYEES.EXPORT}?${queryParams.toString()}`;
    return apiClient.downloadBlobWithFilename(endpoint, { method: 'GET' });
  }
}

export const employeeService = new EmployeeService();
